import { orderedPackage } from 'services/api/pay';
import { useMount, useRequest } from 'ahooks';
import { atom, useAtom, useAtomValue } from 'jotai';
import { filter, isNull, noop, orderBy } from 'lodash-es';
import * as React from 'react';
import { useTranslate } from '@tolgee/react';
import { usePricingCatalog } from './usePricing';
import { catalogToPackageInfoV2 } from '../catalogAdapter';
import { narrowPriceZone } from '../priceZone';

export const comboInfoState = atom<Pay.OrderedPackage[]>([]);

export const useComboInfo = (force?: boolean) => {
  const { state, loading } = useFetch(force);
  if (loading) return [];
  if (state === null) return [];
  return state;
};

let refreshComboInfoFn = noop;

const useFetch = (force?: boolean) => {
  const [state, setState] = useAtom(comboInfoState);
  const { loading, refreshAsync } = useRequest(orderedPackage, {
    onSuccess: (data) => {
      setState(orderBy(data, 'status', 'desc'));
    },
    manual: true,
  });
  useMount(() => {
    if (isNull(state) || force) {
      refreshAsync();
    }
  });

  React.useEffect(() => {
    refreshComboInfoFn = refreshAsync;
  });

  return {
    loading,
    state,
  };
};

/**
 * 这里通过套餐来判断是否是企业版，和通过qaEnable不同，undefined表示接口在loading
 */
export const useIsEnterprisePkg = (): undefined | boolean => {
  const { state, loading } = useFetch();
  if (loading) return undefined;
  if (isNull(state)) return undefined;
  const pkg = filter(state, (v) => v.id !== 8);
  return pkg[0]?.id === 5;
};

export const refreshComboInfo = () => {
  refreshComboInfoFn?.();
};

export const useOrderedPackagesActions = () => {
  const orderedPackages = useAtomValue(comboInfoState);
  const { t } = useTranslate();
  const workingPack = orderedPackages?.find((v) => v.status === 1);
  // 升级判断按当前套餐价区快照的目录；旧套餐（无快照）回退跨境价区
  // （老套餐授权站点不限域，无地理价区可归，跨境方案为约定默认值）
  const workingZone = narrowPriceZone(workingPack?.priceZone);
  const { catalog } = usePricingCatalog(workingZone ?? 'CN', 'ORDER');
  const packageInfo = React.useMemo(() => catalogToPackageInfoV2(catalog, t), [catalog, t]);
  const [canUpgrade, setCanUpgrade] = React.useState(false);

  React.useEffect(() => {
    if (!workingPack || !packageInfo?.packageData.length) {
      return;
    }
    if (workingPack.id === 6) {
      // QikChat
      setCanUpgrade(true);
      return;
    }
    const topAccountNum = Math.max(...packageInfo.packageData.map((item) => item.accountNum));
    setCanUpgrade(workingPack.accountNum < topAccountNum);
  }, [packageInfo, workingPack]);

  // 存在生效中的订阅（stripe 自动续费）时不展示续订
  const canRenew = React.useMemo(() => {
    const autoRenewing = orderedPackages?.find((v) => v.status === 1)?.autoRenewing;
    return !autoRenewing && !orderedPackages?.find((v) => v.status === 0);
  }, [orderedPackages]);

  return {
    canUpgrade,
    canRenew,
  };
};
